import { useEffect, useRef, useState } from "react";
import {
  uploadAcceptString,
  filesAllowedFor,
  getUploadKind,
  CAD_NOT_ALLOWED_MESSAGE,
} from "shared/constants/officeFiles";
import { Icons } from "assets";
import { toastMessage } from "shared/components/common/toast";
import { handleErrors } from "shared/utils/helper";
import {
  UpdateRevisionDesignerUploadsService,
  UploadDesignerFile,
} from "shared/services/revision";
import CustomButton from "shared/components/common/customButton";
import CustomInput from "shared/components/common/customInput";
import CustomTextEditor from "shared/components/common/customTextEditor";
import classNames from "classnames";
import Spinner from "shared/components/common/spinner";
import styles from "./style.module.scss";

export interface EditFileItem {
  id: number;
  src: string;
  type: "image" | "pdf" | "document";
  name?: string;
}

const TITLE_MAX = 240;

const titleProblem = (value: string): string => {
  if (!value.trim()) return "A revision needs a name.";
  if (value.trim().length > TITLE_MAX)
    return `Keep the revision name to ${TITLE_MAX} characters or fewer.`;
  return "";
};

interface Props {
  revisionId: number;
  initialTitle: string;
  initialDesignerComment: string;
  initialInternalComment: string;
  initialFiles: EditFileItem[];
  isFinalPlans?: boolean;
  onClose: () => void;
  onSaved: (title: string) => void;
}

const EditRevisionModal = ({
  revisionId,
  initialTitle,
  initialDesignerComment,
  initialInternalComment,
  initialFiles,
  isFinalPlans = false,
  onClose,
  onSaved,
}: Props) => {
  const [items, setItems] = useState<EditFileItem[]>(initialFiles);
  const [title, setTitle] = useState(initialTitle);
  const [titleError, setTitleError] = useState("");
  const [designerComment, setDesignerComment] = useState(
    initialDesignerComment,
  );
  const [internalComment, setInternalComment] = useState(initialInternalComment);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Drives auto-scroll while a tile is dragged near the top/bottom edge.
  // Native HTML5 drag-and-drop does not scroll containers on its own.
  const autoScrollRef = useRef<{ raf: number; velocity: number }>({
    raf: 0,
    velocity: 0,
  });

  const stopAutoScroll = () => {
    if (autoScrollRef.current.raf) {
      cancelAnimationFrame(autoScrollRef.current.raf);
    }
    autoScrollRef.current.raf = 0;
    autoScrollRef.current.velocity = 0;
  };

  // rAF loop: keeps scrolling while the pointer is held still at an edge,
  // since `dragover` stops firing when the pointer doesn't move.
  const runAutoScroll = () => {
    const el = scrollRef.current;
    if (!el || autoScrollRef.current.velocity === 0) {
      autoScrollRef.current.raf = 0;
      return;
    }
    const before = el.scrollTop;
    el.scrollTop += autoScrollRef.current.velocity;
    if (el.scrollTop === before) {
      // Reached the top/bottom boundary — nothing more to scroll.
      stopAutoScroll();
      return;
    }
    autoScrollRef.current.raf = requestAnimationFrame(runAutoScroll);
  };

  const handleAutoScroll = (clientY: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const EDGE = 60; // px from edge that triggers scrolling
    const MAX_SPEED = 16; // px per frame at the very edge
    const distTop = clientY - rect.top;
    const distBottom = rect.bottom - clientY;
    let velocity = 0;
    if (distTop < EDGE) {
      velocity = -MAX_SPEED * (1 - Math.max(distTop, 0) / EDGE);
    } else if (distBottom < EDGE) {
      velocity = MAX_SPEED * (1 - Math.max(distBottom, 0) / EDGE);
    }
    autoScrollRef.current.velocity = velocity;
    if (velocity !== 0 && !autoScrollRef.current.raf) {
      autoScrollRef.current.raf = requestAnimationFrame(runAutoScroll);
    }
  };

  // Cancel any in-flight scroll loop if the modal unmounts mid-drag.
  useEffect(() => stopAutoScroll, []);

  const handleAddFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const { allowed, rejected } = filesAllowedFor(
      Array.from(fileList),
      isFinalPlans,
    );
    if (rejected.length > 0) {
      toastMessage("error", CAD_NOT_ALLOWED_MESSAGE);
    }
    if (allowed.length === 0) return;
    const files = allowed;
    setUploading(true);
    try {
      for (const file of files) {
        const id = await UploadDesignerFile(file);
        const type = getUploadKind({ mime: file.type, name: file.name });
        setItems((prev) => [
          ...prev,
          { id, src: URL.createObjectURL(file), type, name: file.name },
        ]);
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (id: number) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  // Live reorder: as the dragged tile enters another tile, move it there
  // immediately so the grid visibly rearranges while dragging.
  const handleDragEnter = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) return;
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(draggedIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDraggedIndex(index);
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (titleError && !titleProblem(value)) {
      setTitleError("");
    }
  };

  const handleSave = async () => {
    const problem = titleProblem(title);
    if (problem) {
      setTitleError(problem);
      return;
    }
    if (items.length === 0) {
      toastMessage("error", "A revision must have at least one file.");
      return;
    }
    setSaving(true);
    try {
      const payload = items.map((it, i) => ({ id: it.id, order: i + 1 }));
      const res = await UpdateRevisionDesignerUploadsService(
        revisionId,
        payload,
        {
          title: title.trim(),
          designer_comment: designerComment,
          internal_comment: internalComment,
        },
      );
      if (res.status === 200 || res.status === 201) {
        toastMessage("success", "Revision updated.");
        onSaved(title.trim());
        onClose();
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || uploading;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: 12,
          width: "100%",
          // Fits 5 fixed 140px tiles: 5*140 + 4*12 gap + 2*20 padding + ~16
          // scrollbar ≈ 804. 820 leaves margin so the 5th column always fits.
          maxWidth: 820,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid #e2e8f0",
            flexShrink: 0,
          }}
        >
          <h3 style={{ fontSize: 16, fontWeight: 600, color: "#0f172a" }}>
            Edit Revision
          </h3>
          <button
            type="button"
            onClick={() => !busy && onClose()}
            aria-label="Close"
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <Icons.Cross />
          </button>
        </div>

        <div
          ref={scrollRef}
          style={{
            padding: 20,
            overflowY: "auto",
            // Fill the space between the pinned header/footer and scroll within
            // it. minHeight:0 is required so this flex child can shrink below
            // its content height on short viewports — without it the panel
            // overflows 85vh and the footer overlaps the tiles.
            flex: 1,
            minHeight: 0,
          }}
          onDragOver={(e) => handleAutoScroll(e.clientY)}
          onDrop={stopAutoScroll}
        >
          <CustomInput
            label="Revision Title"
            placeholder="Enter revision title"
            value={title}
            error={titleError}
            required
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleTitleChange(e.target.value)
            }
          />

          <div className={styles.commentField}>
            <CustomTextEditor
              label="Comments"
              value={designerComment}
              onChange={setDesignerComment}
              customContainerStyle={styles.commentsEditor}
              customToolbarStyle={styles.commentsToolbar}
              placeholder="Type your message here..."
            />
            <span className={styles.fieldHint}>
              Visible to the client with this revision.
            </span>
          </div>

          <div className={styles.commentField}>
            <CustomTextEditor
              label="Internal Comments"
              value={internalComment}
              onChange={setInternalComment}
              customContainerStyle={styles.commentsEditor}
              customToolbarStyle={styles.commentsToolbar}
              placeholder="Type your message here..."
            />
            <span className={styles.fieldHint}>
              Visible to the TCH team only.
            </span>
          </div>

          <div className={styles.commentField}>
            <span className={styles.fieldLabel}>Files</span>
            <span className={styles.fieldHint}>
              Add, remove, or drag to reorder the files for this revision.
            </span>
          </div>

          <div
            style={{
              display: "grid",
              // Fixed 140px tracks so tiles never shrink; auto-fill packs as
              // many columns as fit (up to 5 at full width) and drops to fewer
              // when the modal is narrower. Centered so leftover space is even.
              gridTemplateColumns: "repeat(auto-fill, 140px)",
              justifyContent: "center",
              gap: 12,
            }}
          >
            {items.map((item, index) => (
              <div
                key={item.id}
                draggable={!busy}
                onDragStart={(e) => {
                  // Firefox only starts a drag when data is set; without this
                  // non-image tiles are not draggable at all.
                  e.dataTransfer.setData("text/plain", String(item.id));
                  e.dataTransfer.effectAllowed = "move";
                  // Defer the state change: re-rendering the dragged element
                  // during dragstart makes Chrome abort the drag.
                  window.setTimeout(() => setDraggedIndex(index), 0);
                }}
                onDragEnter={() => handleDragEnter(index)}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setDraggedIndex(null);
                  stopAutoScroll();
                }}
                onDragEnd={() => {
                  setDraggedIndex(null);
                  stopAutoScroll();
                }}
                className={classNames(
                  styles.tile,
                  "relative",
                  draggedIndex === index && styles.dragging,
                )}
                style={{ cursor: busy ? "default" : "grab" }}
              >
                <div
                  className={classNames(
                    styles.surface,
                    item.type === "image" && styles.imageSurface,
                  )}
                >
                  {item.type === "image" ? (
                    <img
                      src={item.src}
                      alt={item.name || "file"}
                      draggable={false}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#475569",
                      }}
                    >
                      {item.type === "document" ? (
                        <Icons.DwgIcon />
                      ) : (
                        <Icons.FilePdfIcon />
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => !busy && handleDelete(item.id)}
                    aria-label="Remove file"
                    className={styles.deleteIcon}
                  >
                    <Icons.TrashOutline2 width={15} height={15} />
                  </button>
                </div>
                <span className={styles.fileName} title={item.name || "File"}>
                  {item.name || "File"}
                </span>
              </div>
            ))}

            <div
              className={styles.tile}
              role="button"
              aria-label="Add file"
              onClick={() => !busy && inputRef.current?.click()}
            >
              <div className={styles.addFileBox}>
                {uploading ? (
                  <Spinner />
                ) : (
                  <span className={styles.addFilePlus}>+</span>
                )}
              </div>
              <span className={styles.fileName}>
                {uploading ? "Uploading..." : "Add file"}
              </span>
            </div>
          </div>

          <input
            ref={inputRef}
            type="file"
            multiple
            accept={uploadAcceptString(isFinalPlans)}
            style={{ display: "none" }}
            onChange={(e) => {
              handleAddFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            padding: "14px 20px",
            borderTop: "1px solid #e2e8f0",
            flexShrink: 0,
          }}
        >
          <CustomButton
            title="Cancel"
            onClick={() => !busy && onClose()}
            disabled={busy}
            containerStyle="primary-outline-btn"
          />
          <CustomButton
            title="Save Changes"
            onClick={handleSave}
            loading={saving}
            disabled={busy}
            containerStyle="primary-btn"
          />
        </div>
      </div>
    </div>
  );
};

export default EditRevisionModal;
